import React, {
    Fragment,
    Suspense,
    useEffect,
    useState,
    useCallback,
    lazy,
    memo,
    Dispatch,
    SetStateAction,
} from 'react';
import { IFloorplanViewModel } from '@monofe/ui-workspace/floorplan/interface';
import { IFloorplanControllerProps } from './interface';
import modelPlaceholderData from './placeholderData';
import LoadingView from '@monofe/ui-workspace/common/Loading';
import type IMapping from './mapping';
import { injectLog } from '../../interface/util';
import { getProjectOverrides } from '../../interface/overrides';
import { AnimatePresence } from 'framer-motion';

const MainView = lazy(() => import('@monofe/ui-workspace/floorplan'));
const loadMapper = (): Promise<typeof IMapping> => {
    return new Promise(function (resolve, reject) {
        import('./mapping')
            .then(controller => resolve(controller.default))
            .catch(error => reject(error));
    });
};
interface IFloorplanController {
    model: IFloorplanViewModel;
    isLoading: boolean;
}

const controllerPlaceholderData = (): IFloorplanController => ({
    model: {
        ...modelPlaceholderData(),
    },
    isLoading: true,
});

/* Same as useState, except 3 return-value allow for partial state updates */
function ReactUseStateWithUpdate<S>(
    initialState: S | (() => S),
): [S, Dispatch<SetStateAction<S>>, Dispatch<Partial<S>>] {
    const [getVal, setVal] = useState<S>(initialState);
    const updateVal = useCallback(
        (partial: Partial<S>) => setVal(sPrev => ({ ...sPrev, ...partial })),
        [],
    );
    return [getVal, setVal, updateVal];
}

function Render(props: IFloorplanControllerProps): JSX.Element {
    const log = injectLog();

    const [state, _, updateState] = ReactUseStateWithUpdate<IFloorplanController>(
        controllerPlaceholderData(),
    );

    const onBack = props.onRequestBack;
    const onSiteObject = useCallback(
        (siteObjectId: number) => props.onRequestSiteObject(siteObjectId),
        [props.onRequestSiteObject],
    );

    useEffect(() => {
        loadMapper()
            .then(mapper => mapper(props.route, onSiteObject, onBack))
            .then(model =>
                updateState({
                    model: getProjectOverrides<IFloorplanViewModel>('IFloorplanViewModel', model),
                    isLoading: false,
                }),
            )
            .then(_ => log.info('Loaded Floorplan'))
            .catch(error => log.error(`Failed to load Floorplan ${error}`));
    }, [onBack, onSiteObject, props.route]);

    useEffect(() => {
        document.title = state.model.title.text;
    }, [state.model.title.text]);

    return (
        <AnimatePresence>
            {state.isLoading ? (
                <LoadingView
                    styleOverrides={{
                        opacity: 1,
                        pointerEvents: 'auto',
                    }}
                />
            ) : (
                <Suspense fallback={null}>
                    <MainView key="Floorplan" {...state.model} />
                </Suspense>
            )}
        </AnimatePresence>
    );
}

const propsEqual = (prev: IFloorplanControllerProps, next: IFloorplanControllerProps) => {
    return JSON.stringify(prev.route) === JSON.stringify(next.route);
};

export default memo(Render, propsEqual) as typeof Render;
